import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Home, BarChart3, Newspaper, PenLine, Building2 } from "lucide-react";
import TiltCard from "./TiltCard";
import { useScheduleCall } from "../context/ScheduleCallContext";

const categories = ["All", "Booking", "CRM", "Media", "Enterprise"];

const projects = [
  {
    title: "OnlyHomestays",
    category: "Booking",
    client: "Homestay Marketplace",
    description:
      "Full-featured homestay booking platform with real-time availability, search filters, property listings, and seamless reservation flow for travelers across India.",
    technologies: ["React", "Node.js", "MongoDB", "Payment Gateway"],
    status: "Live",
    highlight: "End-to-end booking engine",
    color: "cyan",
    gradient: "from-accent-cyan/20 to-accent-cyan/5",
    icon: Home,
  },
  {
    title: "OnlyHomestays CRM",
    category: "CRM",
    client: "OnlyHomestays",
    description:
      "Custom CRM built for homestay operators — manage property listings, guest bookings, owner communications, payouts, and analytics from a unified dashboard.",
    technologies: ["React", "Express", "PostgreSQL", "Dashboard"],
    status: "Live",
    highlight: "Property & booking management",
    color: "emerald",
    gradient: "from-accent-emerald/20 to-accent-emerald/5",
    icon: BarChart3,
  },
  {
    title: "Newspews",
    category: "Media",
    client: "News Platform",
    description:
      "Modern news and media platform with content management, category filtering, search, and a responsive reader experience optimized for engagement.",
    technologies: ["React", "Node.js", "CMS", "SEO"],
    status: "Live",
    highlight: "Content-driven media platform",
    color: "violet",
    gradient: "from-accent-violet/20 to-accent-violet/5",
    icon: Newspaper,
  },
  {
    title: "Newspews CRM",
    category: "CRM",
    client: "Newspews",
    description:
      "Editorial CRM for managing articles, writers, publishing workflows, content scheduling, and audience analytics — built to streamline newsroom operations.",
    technologies: ["React", "Express", "MongoDB", "Analytics"],
    status: "Live",
    highlight: "Editorial workflow automation",
    color: "rose",
    gradient: "from-accent-rose/20 to-accent-rose/5",
    icon: PenLine,
  },
  {
    title: "Meamar Estimation Generator",
    category: "Enterprise",
    client: "Construction / Engineering",
    description:
      "Intelligent estimation system that generates accurate project cost estimates — automating calculations, material pricing, and proposal generation for faster client turnaround.",
    technologies: ["React", "Node.js", "PDF Export", "Calculations Engine"],
    status: "Live",
    highlight: "Automated cost estimation",
    color: "primary",
    gradient: "from-primary-600/20 to-primary-400/5",
    icon: Building2,
  },
];

const Portfolio = () => {
  const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true });
  const [selectedCategory, setSelectedCategory] = useState("All");
  const { openScheduleCall } = useScheduleCall();

  const filtered =
    selectedCategory === "All"
      ? projects
      : projects.filter((p) => p.category === selectedCategory);

  return (
    <section id="portfolio" className="section-padding bg-dark-400 relative overflow-hidden">
      <div className="section-container relative z-10">
        <motion.div
          ref={ref}
          className="text-center mb-14"
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <span className="text-accent-cyan font-semibold text-sm uppercase tracking-widest">
            Proven Results
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-bold mt-3 mb-5 gradient-text">
            Projects We've Delivered
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Real products, real clients, real revenue impact. Here's what we've
            built — and what we can build for you.
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          className="flex flex-wrap justify-center gap-3 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
        >
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${
                selectedCategory === cat
                  ? "bg-primary-600 text-white shadow-glow"
                  : "glass-panel text-gray-400 hover:text-white hover:border-primary-500/30"
              }`}
            >
              {cat}
            </button>
          ))}
        </motion.div>

        {/* Project cards */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedCategory}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {filtered.map((project, index) => {
              const ProjectIcon = project.icon;
              return (
              <motion.div
                key={project.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08 }}
                className={index === 0 && filtered.length >= 3 ? "md:col-span-2 lg:col-span-1" : ""}
              >
                <TiltCard glowColor={project.color} className="h-full">
                  <div
                    className={`glass-panel-hover h-full overflow-hidden bg-gradient-to-br ${project.gradient} group`}
                  >
                    {/* Header */}
                    <div className="p-6 pb-4">
                      <div className="flex items-start justify-between mb-4">
                        <div className="text-primary-400">
                          <ProjectIcon className="w-9 h-9" strokeWidth={1.75} aria-hidden />
                        </div>
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-accent-emerald/20 text-accent-emerald border border-accent-emerald/30">
                          {project.status}
                        </span>
                      </div>

                      <h3 className="font-display text-xl font-bold text-white mb-1 group-hover:text-primary-300 transition-colors">
                        {project.title}
                      </h3>
                      <p className="text-sm text-gray-500 mb-3">
                        {project.client}
                      </p>

                      <span className="inline-block px-3 py-1 rounded-lg text-xs font-medium bg-white/5 text-primary-300 border border-white/10">
                        {project.highlight}
                      </span>
                    </div>

                    {/* Body */}
                    <div className="px-6 pb-6">
                      <p className="text-gray-400 text-sm leading-relaxed mb-4">
                        {project.description}
                      </p>

                      <div className="flex flex-wrap gap-2">
                        {project.technologies.map((tech) => (
                          <span
                            key={tech}
                            className="px-2.5 py-1 bg-dark-200/80 text-gray-400 rounded-md text-xs border border-white/5"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </TiltCard>
              </motion.div>
            );
            })}
          </motion.div>
        </AnimatePresence>

        {/* CTA */}
        <motion.div
          className="mt-16 text-center glass-panel p-10 md:p-14"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
        >
          <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-3">
            Your Project Could Be Next
          </h3>
          <p className="text-gray-400 mb-6 max-w-xl mx-auto">
            Whether you need a lead gen site, booking platform, CRM, or AI call
            agent — we deliver production-ready products that generate revenue.
          </p>
          <motion.button
            onClick={openScheduleCall}
            className="btn-primary"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            Schedule a Call →
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Portfolio;
